package com.devi3east.bashbuddy.features.events.detail.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ConfirmationNumber
import androidx.compose.material.icons.filled.EmojiEvents
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.devi3east.bashbuddy.features.common.util.formatPrice
import com.devi3east.bashbuddy.prizeList.domain.models.Prize
import com.devi3east.bashbuddy.prizeList.domain.models.PrizeType
import com.devi3east.bashbuddy.prizeList.ui.components.PrizeCard

@Composable
fun JackpotCard(
    amount: Long,
    prizeList: List<Prize>? = emptyList(),
    modifier: Modifier = Modifier
) {
    if (amount <= 0 && (prizeList.isNullOrEmpty())) return

    var selectedTab by remember { mutableStateOf<String?>(null) }
    val isExpanded = selectedTab != null
    val animatedAlpha by animateFloatAsState(targetValue = if (isExpanded) 1f else 0f)

    // --- Determine which types exist ---
    val hasMainEventPrizes = prizeList?.any { it.prizeType == PrizeType.MAIN_EVENT } == true
    val hasSideRaffles = prizeList?.any { it.prizeType == PrizeType.SIDE_RAFFLE } == true

    // Build toggle options dynamically
    val toggleOptions = when {
        hasMainEventPrizes && hasSideRaffles -> listOf("Prize List", "Side Raffles")
        hasMainEventPrizes -> listOf("Prize List")
        hasSideRaffles -> listOf("Side Raffles")
        else -> emptyList()
    }

    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(20.dp))
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        MaterialTheme.colorScheme.primary.copy(alpha = 0.12f),
                        MaterialTheme.colorScheme.primary.copy(alpha = 0.03f)
                    )
                )
            )
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.primary.copy(alpha = 0.25f),
                shape = RoundedCornerShape(20.dp)
            )
            .padding(vertical = 16.dp, horizontal = 20.dp)
            .animateContentSize()
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            // --- Jackpot amount ---
            if (amount > 0) {
                // --- Header ---
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = "50/50 Jackpot",
                        style = MaterialTheme.typography.titleMedium.copy(
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface
                        ),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
                Text(
                    text = formatPrice(amount),
                    style = MaterialTheme.typography.displaySmall.copy(
                        fontWeight = FontWeight.ExtraBold,
                        color = MaterialTheme.colorScheme.primary,
                        letterSpacing = 1.sp
                    ),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )
            }

            // --- Prize section ---
            if (!prizeList.isNullOrEmpty() && toggleOptions.isNotEmpty()) {
                // --- Toggle row ---
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    toggleOptions.forEach { label ->
                        val isSelected = selectedTab == label
                        val backgroundColor by animateColorAsState(
                            if (isSelected) MaterialTheme.colorScheme.primaryContainer
                            else MaterialTheme.colorScheme.surface.copy(alpha = 0.6f)
                        )
                        val textColor by animateColorAsState(
                            if (isSelected) MaterialTheme.colorScheme.onPrimaryContainer
                            else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                        )

                        val cardShape = RoundedCornerShape(12.dp)

                        Surface(
                            color = backgroundColor,
                            shape = RoundedCornerShape(12.dp),
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                            modifier = Modifier
                                .weight(1f)
                                .clip(cardShape)
                                .clickable {
                                    selectedTab = if (isSelected) null else label
                                }
                                .heightIn(min = 48.dp)
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 8.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = label,
                                    color = textColor,
                                    style = MaterialTheme.typography.bodyLarge,
                                    fontWeight = FontWeight.Bold,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                }

                // --- Expandable list ---
                AnimatedVisibility(
                    visible = isExpanded,
                    enter = expandVertically() + fadeIn(),
                    exit = shrinkVertically() + fadeOut()
                ) {
                    LazyColumn(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 8.dp)
                            .alpha(animatedAlpha)
                            .heightIn(min = 0.dp, max = 320.dp)
                    ) {
                        when (selectedTab) {
                            "Prize List" -> {
                                val sortedPrizes = prizeList
                                    .filter { it.prizeType == PrizeType.MAIN_EVENT }
                                    .sortedBy { it.timeSlotInstant }

                                items(sortedPrizes) { prize ->
                                    PrizeCard(
                                        icon = {
                                            Icon(
                                                imageVector = Icons.Default.EmojiEvents,
                                                contentDescription = null,
                                                tint = MaterialTheme.colorScheme.primary
                                            )
                                        },
                                        timeSlot = prize.timeSlotInstant,
                                        prize = prize.title,
                                        winningNumber = prize.winningNumber,
                                        winningNumberVisible = !prize.winningNumber.isNullOrBlank()
                                    )
                                }
                            }

                            "Side Raffles" -> {
                                val raffles = prizeList.filter { it.prizeType == PrizeType.SIDE_RAFFLE }

                                items(raffles) { raffle ->
                                    PrizeCard(
                                        icon = {
                                            Icon(
                                                imageVector = Icons.Default.ConfirmationNumber,
                                                contentDescription = null,
                                                tint = MaterialTheme.colorScheme.primary
                                            )
                                        },
                                        serialNumber = raffle.serialNumber,
                                        prize = raffle.title,
                                        winningNumber = raffle.winningNumber,
                                        winningNumberVisible = !raffle.winningNumber.isNullOrBlank()
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}